import type { ComponentType } from 'react';
import { Reveal } from '@/components/ui/Reveal';
import { MarketingImage } from '@/components/marketing/MarketingImage';
import { MissionDetailMockup } from '@/components/marketing/mockups/MissionDetailMockup';
import { BosStatusMockup } from '@/components/marketing/mockups/BosStatusMockup';
import { CrossOrgMockup } from '@/components/marketing/mockups/CrossOrgMockup';
import { HeroMapMockup } from '@/components/marketing/mockups/HeroMapMockup';
import type { MarketingImageId } from '@/data/marketingAssets';

interface Card {
  imageId: MarketingImageId;
  Mockup: ComponentType;
  alt: string;
  eyebrow: string;
  title: string;
  body: string;
}

const CARDS: Card[] = [
  {
    imageId: 'missionDetail',
    Mockup: MissionDetailMockup,
    alt: 'Mission-Detail-Panel mit grüner und roter Maßnahme',
    eyebrow: 'Maßnahmen-System',
    title: 'Mission-Progress nur bei richtiger Ausrüstung.',
    body: 'Jeder Einsatz braucht spezifische Module. Hydraulikset für eingeklemmte Personen, Atemschutz für Brände, Wassertank passender Größe. Solange Anforderungen rot sind, läuft der Einsatz nicht weiter.',
  },
  {
    imageId: 'arrest',
    Mockup: BosStatusMockup,
    alt: 'Vehicle-Detail mit Status-Pille 4 — Am Einsatzort',
    eyebrow: 'Echte BOS-Funkstati',
    title: 'Status 1–8 nach deutschem BOS-Standard.',
    body: 'Auf Anfahrt, am Einsatzort, Sprechwunsch, Patient an Bord. Jeder Funkstatus löst die richtige Folge-Aktion aus — automatisch oder per Annahme im Sprechwunsch-Dialog.',
  },
  {
    imageId: 'crossOrg',
    Mockup: CrossOrgMockup,
    alt: 'Cross-Org-Mission mit gesperrter Patientenversorgung',
    eyebrow: 'Cross-Org-Einsätze',
    title: 'Drei Organisationen, ein Einsatz.',
    body: 'Bei Messerangriff oder Geiselnahme zählt die Reihenfolge. Polizei sichert den Tatort, dann darf der Rettungsdienst rein. Echtes Leitstellen-Management.',
  },
  {
    imageId: 'heroMap',
    Mockup: HeroMapMockup,
    alt: 'Karte mit Wachen-Markern in Deutschland',
    eyebrow: 'Echte Karten, echte Adressen',
    title: 'Ganz Deutschland, Österreich und Schweiz.',
    body: 'Wähle deine Heimatstadt. Spiele in echten Straßen mit echten Wachen-Standorten aus OpenStreetMap. Berlin, München, Wien, Zürich — du entscheidest.',
  },
];

export function Showcase() {
  return (
    <section id="showcase" className="relative bg-bg-secondary px-4 py-24 md:px-6 md:py-32">
      <div className="mx-auto max-w-7xl">
        <Reveal>
          <div className="mb-16 max-w-3xl">
            <p className="mb-3 text-sm uppercase tracking-widest text-accent-dispatch">
              Mechaniken im Detail
            </p>
            <h2 className="font-display text-4xl font-bold leading-tight md:text-5xl">
              Disponieren wie im echten Einsatz.
            </h2>
            <p className="mt-4 text-lg text-fg-secondary">
              Vier Kern-Mechaniken, die Dispatch24 von anderen Browser-Spielen abheben.
            </p>
          </div>
        </Reveal>

        <div className="grid gap-6 md:grid-cols-2">
          {CARDS.map((c, i) => (
            <Reveal key={c.imageId + c.title} delay={i * 0.06}>
              <ShowcaseCard card={c} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ShowcaseCard({ card }: { card: Card }) {
  return (
    <article className="group relative overflow-hidden rounded-xl border border-border-subtle bg-bg-primary transition hover:-translate-y-1 hover:border-border-strong hover:shadow-2xl">
      <div className="aspect-video overflow-hidden border-b border-border-subtle bg-bg-secondary">
        <MarketingImage
          id={card.imageId}
          mockupComponent={card.Mockup}
          alt={card.alt}
          width={800}
          height={500}
        />
      </div>
      <div className="p-6 md:p-8">
        <p className="mb-2 text-xs uppercase tracking-widest text-accent-emergency">
          {card.eyebrow}
        </p>
        <h3 className="font-display text-xl font-semibold leading-tight md:text-2xl">
          {card.title}
        </h3>
        <p className="mt-3 text-sm leading-relaxed text-fg-secondary md:text-base">
          {card.body}
        </p>
      </div>
    </article>
  );
}
